import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import SectionLabel from '../components/shared/SectionLabel';
import SubscribeForm from '../components/shared/SubscribeForm';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import { useNewsletters, useNewsletterTypes } from '../hooks/useApi';
import SEO from '../components/shared/SEO';
import { formatDate } from '../utils/formatDate';
import type { INewsletter } from '../types';
import '../styles/insightspage.css';

const knownTypeLabels: Record<string, string> = {
  'defi-update': 'DeFi Update',
  'ai-insights': 'AI Insights',
  'investor-brief': 'Investor Brief',
  'portfolio-spotlight': 'Portfolio Spotlight',
  'rwa-report': 'RWA Report',
  'regulatory-radar': 'Regulatory Radar',
};

function getTypeLabel(newsletter: INewsletter): string {
  if (newsletter.type === 'custom') return newsletter.customType || 'Insight';
  return knownTypeLabels[newsletter.type] || newsletter.type;
}

function getExcerpt(newsletter: INewsletter): string {
  if (newsletter.excerpt) return newsletter.excerpt;
  const firstText = newsletter.sections?.find((s) => s.type === 'text' && s.body);
  if (firstText?.body) {
    const text = firstText.body.replace(/\n/g, ' ').trim();
    return text.length > 180 ? text.slice(0, 180) + '…' : text;
  }
  return '';
}

export default function InsightsPage() {
  const [type, setType] = useState('');
  const [customType, setCustomType] = useState('');
  const [page, setPage] = useState(1);

  const { data: typesData } = useNewsletterTypes();
  const { data, isLoading } = useNewsletters({
    page,
    limit: 9,
    type: type || undefined,
    customType: customType || undefined,
  });

  const newsletters = data?.data || [];
  const pagination = data?.pagination;

  // Build dynamic filter list — each distinct custom type gets its own button
  const dynamicFilters = [
    { typeKey: '', customTypeKey: '', label: 'All' },
    ...(typesData || []).map((t) => ({
      typeKey: t._id,
      customTypeKey: t._id === 'custom' ? (t.customType || '') : '',
      label: t._id === 'custom'
        ? (t.customType || 'Custom')
        : (knownTypeLabels[t._id] || t._id),
    })),
  ];

  // Sort strictly by publish date descending — featured is a visual badge only
  // and must not push older articles ahead of newer ones (see insightspage.css
  // .is-featured for the styling; the backend already returns items in this
  // order, this just protects the page if that ever changes).
  const sorted = [...newsletters].sort((a, b) => {
    const dateA = new Date(a.publishedAt || a.createdAt).getTime();
    const dateB = new Date(b.publishedAt || b.createdAt).getTime();
    return dateB - dateA;
  });

  return (
    <Layout>
      <div className="insights-page">
        <SEO
          title="Insights & Intelligence - Apex DeFi Labs Inc. Thought Leadership"
          description="Expert analysis, market intelligence, and thought leadership from Apex DeFi Labs Inc. on decentralized finance, AI, RWA tokenization, and the future of digital assets."
          url="https://apexholdings.com/insights"
          type="website"
          keywords="DeFi insights, blockchain research, RWA tokenization, crypto market analysis, AI finance"
        />

        <section className="insights-page-hero">
          <div className="insights-page-hero-overlay" />
          <div className="insights-page-shell">
            <SectionLabel>Thought Leadership</SectionLabel>
            <h1 className="insights-page-title">
              Insights &amp; <em>Intelligence</em>
            </h1>
            <p className="insights-page-subtitle">
              Expert analysis, market intelligence, and strategic insights from our research team.
            </p>
          </div>
        </section>

        <section className="insights-page-catalog">
          <div className="insights-page-shell">
            {dynamicFilters.length > 1 && (
              <div className="insights-page-filter-list">
                {dynamicFilters.map((filter) => (
                  <button
                    key={`${filter.typeKey}:${filter.customTypeKey}`}
                    type="button"
                    onClick={() => {
                      setType(filter.typeKey);
                      setCustomType(filter.customTypeKey);
                      setPage(1);
                    }}
                    className={`insights-page-filter${
                      type === filter.typeKey && customType === filter.customTypeKey ? ' is-active' : ''
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            )}

            <div className="insights-page-grid">
              {isLoading
                ? Array.from({ length: 6 }).map((_, index) => (
                    <LoadingSkeleton key={index} className="insights-page-skeleton" />
                  ))
                : sorted.length === 0
                  ? (
                      <div className="insights-page-empty">
                        <p className="insights-page-empty-copy">
                          No insights published yet. Check back soon.
                        </p>
                      </div>
                    )
                  : sorted.map((newsletter) => (
                      <Link
                        key={newsletter._id}
                        to={`/insights/${newsletter.slug}`}
                        className={`insights-page-card${newsletter.featured ? ' is-featured' : ''}`}
                      >
                        <div className="insights-page-card-media">
                          {newsletter.heroImage ? (
                            <>
                              <img
                                src={newsletter.heroImage}
                                alt={newsletter.title}
                                className="insights-page-card-image"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  (e.currentTarget.nextElementSibling as HTMLElement | null)?.removeAttribute('hidden');
                                }}
                              />
                              <span className="insights-page-card-placeholder" hidden>
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                              </span>
                            </>
                          ) : (
                            <span className="insights-page-card-placeholder">
                              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                            </span>
                          )}
                        </div>

                        <div className="insights-page-card-body">
                          {newsletter.featured && (
                            <span className="insights-page-card-tag">
                              <span className="insights-page-featured-badge">⭐ Featured · </span>
                              {getTypeLabel(newsletter)}
                            </span>
                          )}
                          <h2 className="insights-page-card-title">{newsletter.title}</h2>
                          {getExcerpt(newsletter) && (
                            <p className="insights-page-card-excerpt">{getExcerpt(newsletter)}</p>
                          )}
                          <div className="insights-page-card-footer">
                            <p className="insights-page-card-meta">
                              {newsletter.publishedAt ? formatDate(newsletter.publishedAt) : 'Draft'} &middot; 5 min read
                            </p>
                            <span className="insights-page-read-more">Read More →</span>
                          </div>
                        </div>
                      </Link>
                    ))}
            </div>

            {pagination && pagination.totalPages > 1 && (
              <div className="insights-page-pagination">
                <button
                  type="button"
                  disabled={!pagination.hasPrev}
                  onClick={() => setPage((current) => current - 1)}
                  className="insights-page-pagination-button"
                >
                  Previous
                </button>
                <span className="insights-page-pagination-copy">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  type="button"
                  disabled={!pagination.hasNext}
                  onClick={() => setPage((current) => current + 1)}
                  className="insights-page-pagination-button"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="insights-page-subscribe">
          <div className="insights-page-subscribe-shell">
            <h2 className="insights-page-subscribe-title">Stay Informed</h2>
            <p className="insights-page-subscribe-copy">
              Subscribe to receive our latest insights and market intelligence.
            </p>
            <div className="insights-page-subscribe-form">
              <SubscribeForm />
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
